import React, { useMemo, useState } from 'react';
import { X, Plus, Trash2, AlertTriangle, Send } from 'lucide-react';
import templateService, {
  TemplateDraft
} from '../../../services/whatsapp/templateService';
import { TemplateComponent } from '../../../types/models/whatsapp';
import TemplatePreview from './TemplatePreview';
import MediaUploadField from './MediaUploadField';

interface TemplateBuilderProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

type HeaderFormat = 'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
type ButtonType = 'QUICK_REPLY' | 'URL' | 'PHONE_NUMBER';

interface ButtonDraft {
  type: ButtonType;
  text: string;
  url?: string;
  phone_number?: string;
}

const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'en_US', label: 'English (US)' },
  { code: 'hi', label: 'Hindi' },
  { code: 'en_GB', label: 'English (UK)' }
];

/** Name must be lowercase_snake_case — normalize as the user types. */
const normalizeName = (raw: string): string =>
  raw
    .toLowerCase()
    .replace(/[^a-z0-9_\s]/g, '')
    .replace(/\s+/g, '_')
    .slice(0, 60);

const TemplateBuilder: React.FC<TemplateBuilderProps> = ({
  open,
  onClose,
  onCreated
}) => {
  const [name, setName] = useState('');
  const [language, setLanguage] = useState('en');
  const [category, setCategory] =
    useState<TemplateDraft['category']>('UTILITY');
  const [headerFormat, setHeaderFormat] = useState<HeaderFormat>('NONE');
  const [headerText, setHeaderText] = useState('');
  // Meta requires a sample file when submitting a media-header template: it is
  // shown to the reviewer and stored as the example.
  const [headerMediaUrl, setHeaderMediaUrl] = useState('');
  const [body, setBody] = useState('');
  const [footer, setFooter] = useState('');
  const [buttons, setButtons] = useState<ButtonDraft[]>([]);
  const [examples, setExamples] = useState<string[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  /** Variables found in the body, e.g. ['1','2']. */
  const variables = useMemo(() => {
    const found = (body.match(/\{\{(\d+)\}\}/g) || []).map((m) =>
      m.replace(/[^\d]/g, '')
    );
    return Array.from(new Set(found)).sort((a, b) => Number(a) - Number(b));
  }, [body]);

  const components = useMemo((): TemplateComponent[] => {
    const result: TemplateComponent[] = [];

    if (headerFormat !== 'NONE') {
      const header: TemplateComponent = { type: 'HEADER', format: headerFormat };
      if (headerFormat === 'TEXT') {
        header.text = headerText;
      } else if (headerMediaUrl) {
        // header_handle carries the sample URL Meta reviews the template with.
        header.example = { header_handle: [headerMediaUrl] };
      }
      result.push(header);
    }

    const bodyComponent: TemplateComponent = { type: 'BODY', text: body };
    if (variables.length > 0) {
      // Meta wants one example row covering every variable, in order.
      bodyComponent.example = {
        body_text: [variables.map((_, i) => examples[i] || '')]
      };
    }
    result.push(bodyComponent);

    if (footer.trim()) result.push({ type: 'FOOTER', text: footer });

    if (buttons.length > 0) {
      result.push({
        type: 'BUTTONS',
        buttons: buttons.map((b) => ({
          type: b.type,
          text: b.text,
          ...(b.type === 'URL' ? { url: b.url || '' } : {}),
          ...(b.type === 'PHONE_NUMBER'
            ? { phone_number: b.phone_number || '' }
            : {})
        }))
      });
    }

    return result;
  }, [
    headerFormat,
    headerText,
    headerMediaUrl,
    body,
    footer,
    buttons,
    variables,
    examples
  ]);

  if (!open) return null;

  const reset = () => {
    setName('');
    setLanguage('en');
    setCategory('UTILITY');
    setHeaderFormat('NONE');
    setHeaderText('');
    setHeaderMediaUrl('');
    setBody('');
    setFooter('');
    setButtons([]);
    setExamples([]);
    setErrors([]);
    setSubmitted(false);
  };

  const draft = (): TemplateDraft => ({ name, language, category, components });

  const handleValidate = async () => {
    setSaving(true);
    try {
      const result = await templateService.validate(draft());
      setErrors(result.errors);
      return result.valid;
    } catch (err: any) {
      setErrors([err.message || 'Could not validate']);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitted(true);

    // Validate server-side first: Meta blocks a rejected name for 30 days, so
    // a failed submission is expensive in a way a failed check is not.
    const valid = await handleValidate();
    if (!valid) return;

    setSaving(true);
    try {
      await templateService.create(draft());
      reset();
      onCreated();
    } catch (err: any) {
      setErrors([err.message || 'Meta rejected this template']);
    } finally {
      setSaving(false);
    }
  };

  const addButton = (type: ButtonType) => {
    if (buttons.length >= 10) return;
    setButtons([...buttons, { type, text: '' }]);
  };

  const updateButton = (index: number, patch: Partial<ButtonDraft>) =>
    setButtons(buttons.map((b, i) => (i === index ? { ...b, ...patch } : b)));

  const insertVariable = () => {
    const next = variables.length + 1;
    setBody(`${body}{{${next}}}`);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-wa-surface shadow-2xl">
        <div className="flex items-center justify-between border-b border-wa-border px-4 py-3">
          <h2 className="text-sm font-semibold text-wa-text">New template</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-wa-muted hover:bg-wa-surface2"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="grid min-h-0 flex-1 gap-4 overflow-y-auto p-4 md:grid-cols-2">
          {/* ---- Form ---- */}
          <div className="space-y-3">
            <div className="grid gap-2 sm:grid-cols-2">
              <label className="block">
                <span className="text-xs font-medium text-wa-text">Name</span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(normalizeName(e.target.value))}
                  placeholder="trial_reminder"
                  className="mt-1 w-full rounded-lg border-wa-border bg-wa-surface2 font-mono text-xs text-wa-text focus:border-wa-green focus:ring-wa-green"
                />
                <span className="mt-0.5 block text-[10px] text-wa-muted">
                  lowercase, digits and underscores
                </span>
              </label>

              <label className="block">
                <span className="text-xs font-medium text-wa-text">Language</span>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="mt-1 w-full rounded-lg border-wa-border bg-wa-surface2 text-xs text-wa-text focus:border-wa-green focus:ring-wa-green"
                >
                  {LANGUAGES.map((l) => (
                    <option key={l.code} value={l.code}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="block">
              <span className="text-xs font-medium text-wa-text">Category</span>
              <select
                value={category}
                onChange={(e) =>
                  setCategory(e.target.value as TemplateDraft['category'])
                }
                className="mt-1 w-full rounded-lg border-wa-border bg-wa-surface2 text-xs text-wa-text focus:border-wa-green focus:ring-wa-green"
              >
                <option value="UTILITY">Utility — order updates, reminders</option>
                <option value="MARKETING">Marketing — offers, announcements</option>
                <option value="AUTHENTICATION">Authentication — one-time codes</option>
              </select>
              <span className="mt-0.5 block text-[10px] text-wa-muted">
                Meta may re-categorize this, which changes what it costs to send.
              </span>
            </label>

            <label className="block">
              <span className="text-xs font-medium text-wa-text">Header</span>
              <select
                value={headerFormat}
                onChange={(e) => setHeaderFormat(e.target.value as HeaderFormat)}
                className="mt-1 w-full rounded-lg border-wa-border bg-wa-surface2 text-xs text-wa-text focus:border-wa-green focus:ring-wa-green"
              >
                <option value="NONE">None</option>
                <option value="TEXT">Text</option>
                <option value="IMAGE">Image</option>
                <option value="VIDEO">Video</option>
                <option value="DOCUMENT">Document</option>
              </select>
            </label>

            {headerFormat !== 'NONE' && headerFormat !== 'TEXT' && (
              <MediaUploadField
                format={headerFormat}
                value={headerMediaUrl}
                onChange={setHeaderMediaUrl}
                label="Sample file for review"
                required
              />
            )}

            {headerFormat === 'TEXT' && (
              <input
                type="text"
                value={headerText}
                onChange={(e) => setHeaderText(e.target.value.slice(0, 60))}
                placeholder="Header text (max 60)"
                className="w-full rounded-lg border-wa-border bg-wa-surface2 text-xs text-wa-text focus:border-wa-green focus:ring-wa-green"
              />
            )}

            <div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-wa-text">Body</span>
                <button
                  type="button"
                  onClick={insertVariable}
                  className="rounded bg-wa-surface2 px-1.5 py-0.5 font-mono text-[10px] text-wa-muted hover:text-wa-text"
                >
                  {`+ {{${variables.length + 1}}}`}
                </button>
              </div>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value.slice(0, 1024))}
                rows={5}
                placeholder="Hi {{1}}, your trial is confirmed for {{2}}."
                className="mt-1 w-full resize-none rounded-lg border-wa-border bg-wa-surface2 text-xs text-wa-text focus:border-wa-green focus:ring-wa-green"
              />
              <span className="mt-0.5 block text-right text-[10px] text-wa-muted">
                {body.length}/1024
              </span>
            </div>

            {variables.length > 0 && (
              <div className="space-y-1.5">
                <span className="text-xs font-medium text-wa-text">
                  Example values
                </span>
                <p className="text-[10px] text-wa-muted">
                  Meta requires a sample for each variable and rejects the
                  template without them.
                </p>
                {variables.map((position, index) => (
                  <div key={position} className="flex items-center gap-2">
                    <span className="w-10 shrink-0 font-mono text-[11px] text-wa-muted">
                      {`{{${position}}}`}
                    </span>
                    <input
                      type="text"
                      value={examples[index] || ''}
                      onChange={(e) => {
                        const next = [...examples];
                        next[index] = e.target.value;
                        setExamples(next);
                      }}
                      placeholder="e.g. Asha"
                      className="min-w-0 flex-1 rounded-lg border-wa-border bg-wa-surface2 text-xs text-wa-text focus:border-wa-green focus:ring-wa-green"
                    />
                  </div>
                ))}
              </div>
            )}

            <label className="block">
              <span className="text-xs font-medium text-wa-text">
                Footer (optional)
              </span>
              <input
                type="text"
                value={footer}
                onChange={(e) => setFooter(e.target.value.slice(0, 60))}
                placeholder="Reply STOP to unsubscribe"
                className="mt-1 w-full rounded-lg border-wa-border bg-wa-surface2 text-xs text-wa-text focus:border-wa-green focus:ring-wa-green"
              />
            </label>

            <div>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-wa-text">Buttons</span>
                <div className="flex gap-1">
                  {(
                    [
                      ['QUICK_REPLY', 'Reply'],
                      ['URL', 'Link'],
                      ['PHONE_NUMBER', 'Call']
                    ] as Array<[ButtonType, string]>
                  ).map(([type, label]) => (
                    <button
                      key={type}
                      type="button"
                      onClick={() => addButton(type)}
                      className="inline-flex items-center gap-0.5 rounded bg-wa-surface2 px-1.5 py-0.5 text-[10px] text-wa-muted hover:text-wa-text"
                    >
                      <Plus className="h-2.5 w-2.5" />
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-1.5 space-y-1.5">
                {buttons.map((button, index) => (
                  <div key={index} className="flex items-center gap-1.5">
                    <span className="w-16 shrink-0 text-[10px] uppercase text-wa-muted">
                      {button.type === 'QUICK_REPLY'
                        ? 'Reply'
                        : button.type === 'URL'
                          ? 'Link'
                          : 'Call'}
                    </span>
                    <input
                      type="text"
                      value={button.text}
                      onChange={(e) =>
                        updateButton(index, { text: e.target.value.slice(0, 25) })
                      }
                      placeholder="Label"
                      className="min-w-0 flex-1 rounded-lg border-wa-border bg-wa-surface2 text-xs text-wa-text focus:border-wa-green focus:ring-wa-green"
                    />
                    {button.type === 'URL' && (
                      <input
                        type="text"
                        value={button.url || ''}
                        onChange={(e) => updateButton(index, { url: e.target.value })}
                        placeholder="https://…"
                        className="min-w-0 flex-1 rounded-lg border-wa-border bg-wa-surface2 text-xs text-wa-text focus:border-wa-green focus:ring-wa-green"
                      />
                    )}
                    {button.type === 'PHONE_NUMBER' && (
                      <input
                        type="text"
                        value={button.phone_number || ''}
                        onChange={(e) =>
                          updateButton(index, { phone_number: e.target.value })
                        }
                        placeholder="+91…"
                        className="min-w-0 flex-1 rounded-lg border-wa-border bg-wa-surface2 text-xs text-wa-text focus:border-wa-green focus:ring-wa-green"
                      />
                    )}
                    <button
                      type="button"
                      onClick={() => setButtons(buttons.filter((_, i) => i !== index))}
                      className="shrink-0 rounded p-1 text-wa-muted hover:text-danger-500"
                      aria-label={`Remove button ${index + 1}`}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ---- Preview + errors ---- */}
          <div className="space-y-3">
            <span className="text-xs font-medium text-wa-text">Preview</span>
            <TemplatePreview components={components} examples={examples} />

            {submitted && errors.length > 0 && (
              <div className="rounded-lg bg-danger-50 p-3">
                <p className="flex items-center gap-1.5 text-xs font-medium text-danger-600">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  Fix before submitting
                </p>
                <ul className="mt-1.5 list-inside list-disc space-y-0.5 text-[11px] text-danger-600">
                  {errors.map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                </ul>
              </div>
            )}

            <p className="rounded-lg bg-warning-50 p-2 text-[11px] text-warning-700">
              Meta reviews templates asynchronously — usually minutes, sometimes
              hours. A rejected name cannot be reused for 30 days, so check the
              preview carefully.
            </p>
          </div>
        </div>

        <div className="flex gap-2 border-t border-wa-border p-3">
          <button
            type="button"
            onClick={handleValidate}
            disabled={saving}
            className="flex-1 rounded-lg border border-wa-border px-3 py-2 text-sm font-medium text-wa-text hover:bg-wa-surface2 disabled:opacity-50"
          >
            {saving ? 'Checking…' : 'Check for errors'}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || !name.trim() || !body.trim()}
            className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-wa-green px-3 py-2 text-sm font-medium text-white hover:bg-wa-teal disabled:opacity-50"
          >
            <Send className="h-4 w-4" />
            {saving ? 'Submitting…' : 'Submit for approval'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TemplateBuilder;
